<div class="company-page">
  <h1>Company</h1>
  <p class="subtitle">Manage company details, connections, and broker relationships</p>

  <mat-tab-group
    class="company-tabs"
    [selectedIndex]="selectedTabIndex"
    [animationDuration]="'0ms'"
    (selectedTabChange)="onTabChange($event.index)"
    >
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon>apartment</mat-icon>
        <span>Company</span>
        @if (!hasCompany) {
          <span class="tab-start-pill">Start here</span>
        }
      </ng-template>

      <div class="companies-shell">
        <div class="companies-header">
          <h2>Companies</h2>
        </div>

        @if (!hasCompany) {
          <div class="empty-company-state">
            <mat-icon>description</mat-icon>
            <h3>No company assigned</h3>
            <p>A Wallit administrator must provision the company and assign your Payroll Admin access through the existing Companies and Users administration workflow.</p>
          </div>
        }

        @if (hasCompany) {
          <div class="table-shell">
            <table class="companies-table desktop-table">
              <thead>
                <tr>
                  <th>Legal Name</th>
                  <th>DBA</th>
                  <th>EIN</th>
                  <th>Contact</th>
                  <th>SIC Code</th>
                  <th class="actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ company?.name || '--' }}</td>
                  <td>{{ dba }}</td>
                  <td>{{ company?.EIN || '--' }}</td>
                  <td>
                    <div class="contact-name">{{ contactName }}</div>
                    <div class="contact-email">{{ contactEmail }}</div>
                  </td>
                  <td>{{ company?.SIC || '--' }}</td>
                  <td class="actions-col">
                    <button mat-icon-button type="button" aria-label="Edit company" (click)="openEditDialog()">
                      <mat-icon>edit</mat-icon>
                    </button>
                    <button mat-icon-button type="button" aria-label="Delete company" (click)="confirmRemoveCompany()">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="mobile-table-list" aria-label="Companies">
              <article class="mobile-table-card">
                <div class="mobile-card-top">
                  <div class="mobile-card-title">{{ company?.name || '--' }}</div>
                  <div class="mobile-card-actions">
                    <button mat-icon-button type="button" aria-label="Edit company" (click)="openEditDialog()">
                      <mat-icon>edit</mat-icon>
                    </button>
                    <button mat-icon-button type="button" aria-label="Delete company" (click)="confirmRemoveCompany()">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </div>
                </div>
                <div class="mobile-card-summary">
                  <span class="mobile-label">EIN</span>
                  <span class="mobile-value">{{ company?.EIN || '--' }}</span>
                </div>
                <details class="mobile-card-details">
                  <summary>View details</summary>
                  <div class="mobile-field-row">
                    <span class="mobile-field-label">DBA</span>
                    <span class="mobile-field-value">{{ dba }}</span>
                  </div>
                  <div class="mobile-field-row">
                    <span class="mobile-field-label">Contact</span>
                    <span class="mobile-field-value">{{ contactName }}</span>
                  </div>
                  <div class="mobile-field-row">
                    <span class="mobile-field-label">Email</span>
                    <span class="mobile-field-value">{{ contactEmail }}</span>
                  </div>
                  <div class="mobile-field-row">
                    <span class="mobile-field-label">SIC Code</span>
                    <span class="mobile-field-value">{{ company?.SIC || '--' }}</span>
                  </div>
                </details>
              </article>
            </div>
          </div>
        }

      </div>
    </mat-tab>

    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon>link</mat-icon>
        <span>Connections</span>
        @if (hasCompany && needsPayrollConnection) {
          <span class="tab-setup-pill">
            <mat-icon>priority_high</mat-icon>
            <span>Set up</span>
          </span>
        }
      </ng-template>
      <ng-template matTabContent>
        @if (!hasCompany) {
          <section class="setup-cta setup-cta-inline">
            <div class="setup-cta-content">
              <mat-icon>business</mat-icon>
              <div class="setup-cta-copy">
                <h2>Create your first company</h2>
                <p>To use Connections, create a company first in Company &gt; Company.</p>
              </div>
            </div>
            <button mat-raised-button color="primary" type="button" (click)="onGoToCompanyTab()">
              Go to Company
            </button>
          </section>
        }

        @if (hasCompany) {
          <app-payroll-connections></app-payroll-connections>
        }
      </ng-template>
    </mat-tab>

    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon>group</mat-icon>
        <span>Broker</span>
      </ng-template>
      @if (!hasCompany) {
        <section class="setup-cta setup-cta-inline">
          <div class="setup-cta-content">
            <mat-icon>business</mat-icon>
            <div class="setup-cta-copy">
              <h2>Create your first company</h2>
              <p>To use Broker, create a company first in Company &gt; Company.</p>
            </div>
          </div>
          <button mat-raised-button color="primary" type="button" (click)="onGoToCompanyTab()">
            Go to Company
          </button>
        </section>
      }

      @if (hasCompany) {
        <div class="brokers-shell">
          <div class="brokers-header">
            <h2>Brokers</h2>
            <button class="button" color="primary" mat-raised-button type="button" (click)="openAddBrokerDialog()">
              <mat-icon>add</mat-icon>
              Add Broker
            </button>
          </div>
          @if (unassignedBrokerCategories.length > 0) {
            <div class="broker-alert">
              <div class="broker-alert-title">
                <mat-icon>warning</mat-icon>
                <span>Unassigned Benefit Categories</span>
              </div>
              <p>The following benefit categories do not have a broker assigned:</p>
              <div class="broker-alert-chips">
                @for (category of unassignedBrokerCategories; track category) {
                  <span class="broker-chip">{{ category }}</span>
                }
              </div>
            </div>
          }
          @if (!hasBrokers) {
            <div class="empty-broker-state">
              <mat-icon>groups</mat-icon>
              <h3>No brokers yet</h3>
              <p>Add a broker to manage your benefit category relationships</p>
              <button class="button" color="primary" mat-raised-button type="button" (click)="openAddBrokerDialog()">
                <mat-icon>add</mat-icon>
                Add Broker
              </button>
            </div>
          }
          @if (hasBrokers) {
            <div class="table-shell brokers-table-shell">
              <table class="brokers-table desktop-table">
                <thead>
                  <tr>
                    <th>Broker Company</th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Assigned Categories</th>
                    <th class="actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  @for (broker of brokers; track trackBroker($index, broker)) {
                    <tr>
                      <td>{{ broker.companyName || '--' }}</td>
                      <td>{{ broker.contactName || '--' }}</td>
                      <td>{{ broker.email || '--' }}</td>
                      <td>{{ broker.phone || '--' }}</td>
                      <td>
                        <div class="assigned-categories">
                          @for (category of broker.categories; track category) {
                            <span class="assigned-chip">{{ category }}</span>
                          }
                        </div>
                      </td>
                      <td class="actions-col">
                        <button mat-icon-button type="button" aria-label="Edit broker" (click)="openEditBrokerDialog(broker)">
                          <mat-icon>edit</mat-icon>
                        </button>
                        <button mat-icon-button type="button" aria-label="Delete broker" (click)="removeBroker(broker)">
                          <mat-icon>delete</mat-icon>
                        </button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
              <div class="mobile-table-list" aria-label="Brokers">
                @for (broker of brokers; track trackBroker($index, broker)) {
                  <article class="mobile-table-card">
                    <div class="mobile-card-top">
                      <div class="mobile-card-title">{{ broker.companyName || '--' }}</div>
                      <div class="mobile-card-actions">
                        <button mat-icon-button type="button" aria-label="Edit broker" (click)="openEditBrokerDialog(broker)">
                          <mat-icon>edit</mat-icon>
                        </button>
                        <button mat-icon-button type="button" aria-label="Delete broker" (click)="removeBroker(broker)">
                          <mat-icon>delete</mat-icon>
                        </button>
                      </div>
                    </div>
                    <div class="mobile-card-summary">
                      <span class="mobile-label">Contact</span>
                      <span class="mobile-value">{{ broker.contactName || '--' }}</span>
                    </div>
                    <div class="mobile-card-summary">
                      <span class="mobile-label">Email</span>
                      <span class="mobile-value">{{ broker.email || '--' }}</span>
                    </div>
                    <details class="mobile-card-details">
                      <summary>View details</summary>
                      <div class="mobile-field-row">
                        <span class="mobile-field-label">Phone</span>
                        <span class="mobile-field-value">{{ broker.phone || '--' }}</span>
                      </div>
                      <div class="mobile-field-row mobile-chip-row">
                        <span class="mobile-field-label">Assigned Categories</span>
                        <div class="assigned-categories mobile-assigned-categories">
                          @for (category of broker.categories; track category) {
                            <span class="assigned-chip">{{ category }}</span>
                          }
                        </div>
                      </div>
                    </details>
                  </article>
                }
              </div>
            </div>
          }
        </div>
      }
    </mat-tab>
  </mat-tab-group>
</div>
